import { apiPaths } from 'configs/apiConfigs'
import { getUserProfile, settingsServiceApiPaths, setUserPreference } from 'features/settings/settingsApi'
import { UserPreferencesKeysEnum } from 'features/settings/settingsTypes'
import { httpClaims } from './http'
import { notificator } from 'utils/notificator/notificator'
import { setTenantIdInStorage, setTntIdInStorage } from './tenantUrlUtils'

export const SELECTED_TENANT_UNAVAILABLE = 'SELECTED_TENANT_UNAVAILABLE'

// Storage key for tracking if tenant switch is manual or automatic
const TENANT_SELECTION_MODE_KEY = 'tenant_selection_mode'
const MANUAL_SELECTION_VALUE = 'manual'

/**
 * Checks if the current/pending tenant switch is a manual user action
 * Returns true if user manually selected from dropdown, false otherwise
 */
export const isManualTenantSwitch = (): boolean => {
  return localStorage.getItem(TENANT_SELECTION_MODE_KEY) === MANUAL_SELECTION_VALUE
}

/**
 * Marks the next tenant switch as a manual user action
 * This should be called when user explicitly selects a tenant from dropdown
 */
export const setManualTenantSwitch = (): void => {
  localStorage.setItem(TENANT_SELECTION_MODE_KEY, MANUAL_SELECTION_VALUE)
}

/**
 * Clears the manual tenant switch flag
 * This should be called after page reload or when switching is complete
 */
export const clearTenantSelectionMode = (): void => {
  localStorage.removeItem(TENANT_SELECTION_MODE_KEY)
}

interface RetrieveTenantIdParams {
  claimId: string
  profile?: any  // User profile containing tenant list
  onSuccess?: (tenantId: string) => void
  onError?: (message: string) => void
}

/**
 * Retrieves tenant ID for a claim and switches to it if user has access.
 * This is used when a claim is accessed but belongs to a different tenant than currently selected.
 * The function will:
 * 1. Fetch the tenant ID from the backend for the given claim
 * 2. Store it in session storage
 * 3. Show a notification to the user
 * 4. Reload the page to apply the tenant switch
 * 
 * @param claimId - The claim ID to retrieve tenant for
 * @param onSuccess - Optional callback when tenant is successfully retrieved
 * @param onError - Optional callback when an error occurs
 */
export const retrieveTenantId = async ({
  claimId,
  profile,
  onSuccess,
  onError,
}: RetrieveTenantIdParams): Promise<void> => {
  try {
    const response = await httpClaims.get(apiPaths.claimsTenant(claimId))
    const tenantId = response.data

    if (tenantId && typeof tenantId === 'string' && tenantId.trim().length > 0) {
      // Find the tntId (integer) from the profile using the tenantId (GUID)
      const tenant = profile?.tenants?.find((t: any) => t.tenantId === tenantId)
      
      if (!tenant || !tenant.tntId) {
        // User doesn't have access to this tenant (different customer or no permission)
        const errorMessage = 'The requested Practice is unavailable or you do not have access to it.'
        sessionStorage.setItem(SELECTED_TENANT_UNAVAILABLE, errorMessage)
        console.error('[retrieveTenantId]', errorMessage)
        if (onError) {
          onError(errorMessage)
        }
        return
      }

      // Store notification message for display after reload
      sessionStorage.setItem(
        SELECTED_TENANT_UNAVAILABLE,
        'Please wait while we switch you to the correct Practice'
      )

      // Mark this as an automatic tenant switch (not manual user action)
      clearTenantSelectionMode()

      // Store BOTH tntId (integer) and tenantId (GUID)
      setTntIdInStorage(tenant.tntId)              // For URLs
      setTenantIdInStorage(tenantId)               // For API headers
      sessionStorage.setItem(UserPreferencesKeysEnum.outboundAiPreferredTenant, tenantId)

      // Call success callback if provided
      if (onSuccess) {
        onSuccess(tenantId)
      }

      // Show notification to user
      notificator.notify({
        heading: 'Switching to correct practice',
        message: 'Please wait while we redirect you...',
        type: 'info',
      })

      // Instead of reload, redirect directly to correct tenant URL to avoid multiple reloads
      // Extract path after current tenant ID and build new URL with correct tenant
      const currentPath = window.location.pathname
      const pathParts = currentPath.split('/')
      const pathAfterTenant = pathParts.length > 2 ? '/' + pathParts.slice(2).join('/') : '/claims'
      
      // Navigate directly to correct tenant with same path (single navigation)
      window.location.href = `/${tenant.tntId}${pathAfterTenant}`
    } else {
      const errorMessage = 'The requested Practice is unavailable or you do not have access to it.'
      sessionStorage.setItem(SELECTED_TENANT_UNAVAILABLE, errorMessage)
      console.error('[retrieveTenantId]', errorMessage)
      if (onError) {
        onError(errorMessage)
      }
    }
  } catch (error: any) {
    const errorMessage = 'The requested Practice is unavailable or you do not have access to it.'
    sessionStorage.setItem(SELECTED_TENANT_UNAVAILABLE, errorMessage)
    console.error('[retrieveTenantId]', error?.response?.data?.message || 'Failed to retrieve tenant information', error)
    if (onError) {
      onError(errorMessage)
    }
  }
}

